import { slicedToArray as _slicedToArray, defineProperty as _defineProperty, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useEffect, Fragment } from 'react';
import { TableContainer, Table as Table$1 } from '@chakra-ui/react';
import { CoreTableHeader } from './table-header.js';
import { CoreTableRows } from './table-row.js';
import { useReactTable, getPaginationRowModel, getExpandedRowModel, getFilteredRowModel, getSortedRowModel, getCoreRowModel } from '@tanstack/react-table';
import { TablePaginationHeader } from './table-pagination-header.js';
import { TablePaginationFooter } from './table-pagination-footer.js';
import { SetColumnProps } from './utils/table-utils.js';
import { DesignSystem } from '@everfi/design-system';
import { ActionBar } from '../../../components/Table/ActionBar.js';
import { SelectedItemsRibbon } from '../../../components/Table/SelectedItemsRibbon.js';
import { ProgressSpinner } from '../../../components/progress-spinner/component.js';
var BorderWidths = DesignSystem.BorderWidths,
  Radii = DesignSystem.Radii;
var toSelectionMap = function toSelectionMap(selectionArray) {
  if (!(selectionArray !== null && selectionArray !== void 0 && selectionArray.length)) {
    return {};
  }
  return selectionArray.reduce(function (selectionMap, value) {
    return Object.assign(selectionMap, _defineProperty({}, value, true));
  }, {});
};
var getRowId = function getRowId(row) {
  return row.id;
};
var Table = function Table(_ref) {
  var columns = _ref.columns,
    data = _ref.data,
    _ref$dataListProps = _ref.dataListProps,
    dataListProps = _ref$dataListProps === void 0 ? {} : _ref$dataListProps,
    filterTable = _ref.filterTable,
    initialValue = _ref.initialValue,
    initialSelection = _ref.initialSelection,
    isLoadingData = _ref.isLoadingData,
    onPageChange = _ref.onPageChange,
    pagingInfo = _ref.pagingInfo,
    select = _ref.select,
    tableOptions = _ref.tableOptions,
    totalCount = _ref.totalCount,
    _ref$useCoreHeader = _ref.useCoreHeader,
    useCoreHeader = _ref$useCoreHeader === void 0 ? true : _ref$useCoreHeader,
    variant = _ref.variant;
  var _useState = useState(function () {
      return toSelectionMap(initialSelection);
    }),
    _useState2 = _slicedToArray(_useState, 2),
    rowSelection = _useState2[0],
    setRowSelection = _useState2[1];
  var _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    globalFilter = _useState4[0],
    setGlobalFilter = _useState4[1];
  var _useState5 = useState([]),
    _useState6 = _slicedToArray(_useState5, 2),
    sorting = _useState6[0],
    setSorting = _useState6[1];
  var _useState7 = useState({}),
    _useState8 = _slicedToArray(_useState7, 2),
    expanded = _useState8[0],
    setExpanded = _useState8[1];

  // updates initial selections when they are calculated values going into selectRow
  useEffect(function () {
    setRowSelection(toSelectionMap(initialSelection));
  }, [initialSelection]);

  // set needed column props (if missing) for react table features
  SetColumnProps(columns);
  var table = useReactTable({
    columns: columns,
    data: data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting: sorting,
      rowSelection: rowSelection,
      expanded: expanded,
      globalFilter: globalFilter
    },
    onExpandedChange: setExpanded,
    getSubRows: function getSubRows(row) {
      return row.subRows;
    },
    enableRowSelection: true,
    enableSortingRemoval: false,
    // disabling option to remove sorting
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: pagingInfo === null || pagingInfo === void 0 ? void 0 : pagingInfo.pageCount,
    rowCount: (pagingInfo === null || pagingInfo === void 0 ? void 0 : pagingInfo.totalCount) || totalCount,
    getRowId: getRowId
  });

  // set initial page size and default sorting
  useEffect(function () {
    table.setPageSize((pagingInfo === null || pagingInfo === void 0 ? void 0 : pagingInfo.pageSize) || 20);
    if (tableOptions !== null && tableOptions !== void 0 && tableOptions.defaultSorted) {
      tableOptions.defaultSorted.map(function (sorted) {
        var id = sorted.dataField;
        var order = sorted.order !== 'asc';
        setSorting([{
          id: id,
          desc: order
        }]);
      });
    }
  }, []);

  // reset page size based on table options
  useEffect(function () {
    if (pagingInfo !== null && pagingInfo !== void 0 && pagingInfo.pageSize) {
      table.setPageSize(pagingInfo.pageSize);
      setActivePageState(0);
    }
  }, [pagingInfo === null || pagingInfo === void 0 ? void 0 : pagingInfo.pageSize]);
  var activePage = table.getState().pagination.pageIndex;
  var _useState9 = useState(pagingInfo === null || pagingInfo === void 0 ? void 0 : pagingInfo.activePage),
    _useState0 = _slicedToArray(_useState9, 2),
    activePageState = _useState0[0],
    setActivePageState = _useState0[1];
  useEffect(function () {
    if (activePage !== undefined && activePage !== activePageState) {
      setActivePageState(activePageState);
    }
  }, [activePageState]);
  var onPageChangeHandler = function onPageChangeHandler(newActivePage) {
    setActivePageState(newActivePage);
    onPageChange && onPageChange(newActivePage);
  };
  var actionBar = dataListProps.actionBar,
    selectedItems = dataListProps.selectedItems;
  var handleRibbonClear = function handleRibbonClear() {
    table.toggleAllRowsSelected(false);
    selectedItems === null || selectedItems === void 0 || selectedItems.onClearSelection();
  };
  var renderTable = function renderTable() {
    return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(SelectedItemsRibbon, _extends({}, selectedItems, {
      onClearSelection: handleRibbonClear
    })), useCoreHeader && /*#__PURE__*/React.createElement(TablePaginationHeader, {
      table: table,
      filterTable: filterTable,
      globalFilter: globalFilter,
      setGlobalFilter: setGlobalFilter,
      activePageState: activePageState,
      initialValue: initialValue,
      pagingInfo: pagingInfo
    }), /*#__PURE__*/React.createElement(TableContainer, {
      rounded: Radii.LG,
      borderWidth: BorderWidths.XS,
      whiteSpace: 'normal'
    }, actionBar && selectedItems && /*#__PURE__*/React.createElement(ActionBar, {
      buttons: actionBar,
      disabled: (selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.count) === 0,
      rows: table.getPrePaginationRowModel().rows
    }), /*#__PURE__*/React.createElement(Table$1, {
      variant: variant,
      "data-object-type": "table"
    }, /*#__PURE__*/React.createElement(CoreTableHeader, {
      table: table,
      select: select,
      tableOptions: tableOptions,
      dataListProps: dataListProps
    }), /*#__PURE__*/React.createElement(CoreTableRows, {
      table: table,
      select: select,
      columns: columns,
      noDataIndication: tableOptions === null || tableOptions === void 0 ? void 0 : tableOptions.noDataText,
      onRowClick: tableOptions === null || tableOptions === void 0 ? void 0 : tableOptions.onRowClick,
      dataListProps: dataListProps
    }), table.getPageCount() > 1 ? /*#__PURE__*/React.createElement(TablePaginationFooter, {
      table: table,
      data: data,
      columnCount: columns.length,
      onPageChange: onPageChangeHandler,
      pagingInfo: pagingInfo,
      activePageState: activePageState
    }) : '')));
  };
  var loading = isLoadingData === true;
  return /*#__PURE__*/React.createElement("div", {
    className: loading ? 'core-table-loading' : undefined
  }, loading && /*#__PURE__*/React.createElement(ProgressSpinner, {
    containerClassName: "everfi-list-loader"
  }), renderTable());
};
Table.displayName = "Table";
export { Table };